import { useI18n } from '../../i18n';
import { DotIcon } from '../shared/Icons';
import type { Bout, BoutVerdict, PublicRoundScore } from '../../types';

interface PublicScoreboardProps {
  bout: Bout;
  publicScores: PublicRoundScore[];
  verdict: BoutVerdict | null;
}

function buildPublicRows(publicScores: PublicRoundScore[]) {
  const byRound: Record<number, {
    red: number; blue: number; count: number;
    redW: number; blueW: number; redKD: number; blueKD: number;
  }> = {};
  for (const s of publicScores) {
    if (!byRound[s.round_number])
      byRound[s.round_number] = { red: 0, blue: 0, count: 0, redW: 0, blueW: 0, redKD: 0, blueKD: 0 };
    byRound[s.round_number].red    += Number(s.red_points);
    byRound[s.round_number].blue   += Number(s.blue_points);
    byRound[s.round_number].count  += 1;
    // Warnings + KDs are already MAX-aggregated server-side per round —
    // take the per-round max across API rows just in case multiple arrived.
    byRound[s.round_number].redW   = Math.max(byRound[s.round_number].redW,   Number(s.red_warnings   ?? 0));
    byRound[s.round_number].blueW  = Math.max(byRound[s.round_number].blueW,  Number(s.blue_warnings  ?? 0));
    byRound[s.round_number].redKD  = Math.max(byRound[s.round_number].redKD,  Number(s.red_knockdowns  ?? 0));
    byRound[s.round_number].blueKD = Math.max(byRound[s.round_number].blueKD, Number(s.blue_knockdowns ?? 0));
  }
  return Object.entries(byRound)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([rnd, v]) => ({
      round_number: Number(rnd),
      red:  Math.round(v.red  / v.count),
      blue: Math.round(v.blue / v.count),
      redW: v.redW, blueW: v.blueW,
      redKD: v.redKD, blueKD: v.blueKD,
    }));
}

// Warning + knock-down badges — styled identically to the judge scorecard
// (see `Marks` in JuryScoringTable.tsx) so spectators see the same visual
// vocabulary at every surface of the app.
function WTags({ count, title }: { count: number; title: string }) {
  if (!count) return null;
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i}
          className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-black bg-amber-100 text-amber-700 border border-amber-300 ml-0.5"
          title={title}>W</span>
      ))}
    </>
  );
}

function KDTags({ count, title }: { count: number; title: string }) {
  if (!count) return null;
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i}
          className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-black bg-red-100 text-red-700 border border-red-300 ml-0.5"
          title={title}>K</span>
      ))}
    </>
  );
}

export default function PublicScoreboard({ publicScores, verdict }: PublicScoreboardProps) {
  const { t } = useI18n();

  if (publicScores.length === 0) return null;
  if (verdict && verdict.outcome !== 'WP') return null;

  const roundRows = buildPublicRows(publicScores);
  const totalRed  = roundRows.reduce((a, r) => a + r.red,  0);
  const totalBlue = roundRows.reduce((a, r) => a + r.blue, 0);

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-sm text-gray-900">{t.bout.roundTotals}</h3>
        {/* #531 — the legend names the letters of the markers below; cautions
            are for the officials only, so they are not here. */}
        <p data-testid="round-totals-legend" className="text-xs text-gray-400 mt-0.5">{t.bout.roundTotalsLegend}</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-xs text-gray-500 uppercase border-b border-gray-200">
            <th className="px-4 py-2 text-left">{t.common.round}</th>
            <th className="px-4 py-2 text-center text-red-500"><DotIcon className="w-6 h-6 mx-auto" /></th>
            <th className="px-4 py-2 text-center text-blue-500"><DotIcon className="w-6 h-6 mx-auto" /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {roundRows.map(r => (
            <tr key={r.round_number}>
              <td className="px-4 py-3 font-semibold text-gray-700">{t.bout.roundN.replace('{n}', String(r.round_number))}</td>
              {/* Score cell uses a 3-column grid so the number stays locked
                  to the same X across every row; badges flow to the right. */}
              <td className="px-4 py-3">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                  <span />
                  <span className={`font-black font-mono text-base ${r.red > r.blue ? 'text-red-600' : 'text-gray-500'}`}>{r.red}</span>
                  <span className="flex flex-wrap items-center pl-1">
                    <WTags  count={r.redW}  title={t.bout.markWarning} />
                    <KDTags count={r.redKD} title={t.bout.markKnockdown} />
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                  <span />
                  <span className={`font-black font-mono text-base ${r.blue > r.red ? 'text-blue-600' : 'text-gray-500'}`}>{r.blue}</span>
                  <span className="flex flex-wrap items-center pl-1">
                    <WTags  count={r.blueW}  title={t.bout.markWarning} />
                    <KDTags count={r.blueKD} title={t.bout.markKnockdown} />
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-gray-50 border-t-2 border-gray-300">
            <td className="px-4 py-2 text-xs font-bold text-gray-700 uppercase">{t.common.total}</td>
            {/* Totals intentionally omit W/K badges — warnings + knockdowns
                are round-level events. Summing across rounds obscures which
                round they happened in and risks misreading a 2-in-one-round
                event as "2 warnings overall". */}
            <td className="px-4 py-2">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                <span />
                <span className="font-black font-mono text-red-700">{totalRed}</span>
                <span />
              </div>
            </td>
            <td className="px-4 py-2">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                <span />
                <span className="font-black font-mono text-blue-700">{totalBlue}</span>
                <span />
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
